import useEvent from "react-use-event-hook";

import {
  EditFormMode,
  EntitiesWithoutPreCreateForm,
  RequestBodyModel,
  RydeAppUnit,
} from "@ryde/shared";

import { useCrudFormCleanup } from "../hooks";
import { useBookingsNavigation } from "./use-bookings-navigation";
import { useCrudRoutes } from "./use-crud-routes";
import { useRidesNavigation } from "./use-rides-navigation";
import { useSimpleNavigation } from "./use-simple-navigation";

export const useCrudNavigate = (entity: RydeAppUnit) => {
  const appNavigate = useCrudRoutes();
  const { navigateHome } = useSimpleNavigation();

  const {
    navigateRidesAdd,
    navigateRidesEdit,
    navigateRidesGridOrKanbanOrMap,
  } = useRidesNavigation();
  const {
    navigateBookingsAdd,
    navigateBookingsEdit,
    navigateBookingsGridOrCalendar,
  } = useBookingsNavigation();

  const { resetEditFormPreviousData } = useCrudFormCleanup(
    entity,
    EditFormMode.CrudRoute,
  );

  const navigateToList = useEvent((keepQuery = true): void => {
    if (entity === "unknown" || !appNavigate[entity]) {
      return navigateHome();
    }

    if (entity === "booking") {
      return navigateBookingsGridOrCalendar(keepQuery);
    }

    if (entity === "ride") {
      return navigateRidesGridOrKanbanOrMap(keepQuery);
    }

    return appNavigate[entity].list(keepQuery);
  });

  const navigateToEdit = useEvent((id: number, keepQuery = true): void => {
    // Ride edit keeps cached dto across switches; cleanup runs in navigateRidesEdit.
    if (entity !== "ride") {
      resetEditFormPreviousData();
    }

    if (entity === "unknown" || !id || !appNavigate[entity]) {
      return navigateHome();
    }

    if (entity === "booking") {
      return navigateBookingsEdit(id, keepQuery);
    }

    if (entity === "ride") {
      return navigateRidesEdit({ id, keepQuery });
    }

    return appNavigate[entity].edit(id, keepQuery);
  });

  const navigateToPreCreateOrNew = useEvent(
    async (keepQuery = true, initialData?: RequestBodyModel): Promise<void> => {
      if (entity === "unknown" || !appNavigate[entity]) {
        return navigateHome();
      }

      resetEditFormPreviousData({ initialData });

      if (entity === "ride") {
        return navigateRidesAdd({ keepQuery, initialData });
      }

      if (entity === "booking") {
        return navigateBookingsAdd(keepQuery, initialData);
      }

      if (EntitiesWithoutPreCreateForm.includes(entity)) {
        return appNavigate[entity].add(undefined, keepQuery);
      }

      return appNavigate[entity].new(keepQuery);
    },
  );
  const navigateToAddOrDuplicate = useEvent(
    (
      duplicateFromId: number | undefined,
      keepQuery = true,
      initialData?: RequestBodyModel,
    ): void => {
      if (entity === "unknown" || !appNavigate[entity]) {
        return navigateHome();
      }

      resetEditFormPreviousData({ initialData, skipFormReset: true });

      return appNavigate[entity].add(duplicateFromId, keepQuery);
    },
  );

  return {
    navigateToList,
    navigateToEdit,
    navigateToAddOrDuplicate,
    navigateToPreCreateOrNew,
  };
};
